/**
 * GamificationChips — iter-170an follow-up #6.
 *
 * Quiet gamification surface on the dashboard. Two micro-components:
 *
 *   1. ``<StreakChip />`` — small flame chip with the consecutive-day
 *      count. Hidden when streak == 0 (no nag).
 *
 *   2. ``<AchievementToast />`` — fires sonner toasts (one per
 *      newly_unlocked slug) and POSTs ack so it doesn't fire again.
 *
 * iter-184-2 — On milestone streaks (30d, 90d) we also surface a
 * downloadable share card so users can post to social. Cheapest
 * organic-referral lever we have.
 *
 * Together they make Lumfi feel rewarding without ever blocking the
 * user's actual workflow. No leaderboards, no daily-quota nags.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Flame, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { tx } from "../../i18n";
import { StreakShareCard } from "./StreakShareCard";


// Milestones that trigger the share-card auto-open. Re-enterable via
// the StreakChip's "Share" affordance once unlocked.
const SHARE_MILESTONES = new Set(["streak_30d", "streak_90d"]);


export function StreakChip({ lang, userName }) {
  const { data } = useQuery({
    queryKey: ["gamification-me"],
    queryFn: async () => (await api.get("/gamification/me")).data,
    staleTime: 60_000,
    retry: false,
  });

  const [shareSlug, setShareSlug] = useState(null);

  // Fire one toast per newly_unlocked achievement and ack each. For
  // milestone streaks (30d, 90d) we ALSO pop the share-card dialog so
  // the user can grab the brag image immediately.
  useEffect(() => {
    if (!data?.newly_unlocked?.length) return;
    const map = Object.fromEntries((data.achievements || []).map((a) => [a.slug, a]));
    data.newly_unlocked.forEach(async (slug) => {
      const a = map[slug];
      if (!a) return;
      toast.success(
        `${a.icon} ${(lang === "en" ? a.label_en : a.label_es)}`,
        {
          description: tx(lang,
            "Quiet milestone unlocked.",
            "Logro silencioso desbloqueado.",
            "Conquista silenciosa desbloqueada."),
          duration: 4000,
        },
      );
      if (SHARE_MILESTONES.has(slug) && !shareSlug) {
        setShareSlug(slug);
      }
      try { await api.post(`/gamification/ack/${slug}`); } catch { /* silent */ }
    });
  }, [data?.newly_unlocked, data?.achievements, lang, shareSlug]);

  const streak = data?.streak || 0;
  // The chip itself appears for any non-zero streak. Once a milestone
  // is reached we surface a tiny share affordance next to the count so
  // the user can re-download the card any time (e.g., to repost).
  const eligibleMilestone =
    streak >= 90 ? "streak_90d" : streak >= 30 ? "streak_30d" : null;
  if (streak === 0) return null;
  return (
    <>
      <span
        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-orange-100 text-orange-700 text-[11px] font-semibold"
        data-testid="dashboard-streak-chip"
        title={tx(lang, `${streak}-day streak`, `Racha de ${streak} días`, `Sequência de ${streak} dias`)}
      >
        <Flame className="h-3 w-3" />
        {streak} {tx(lang, "d", "d", "d")}
        {eligibleMilestone && (
          <button /* design-system: ALLOW legacy-todo */
            type="button"
            onClick={() => setShareSlug(eligibleMilestone)}
            className="ml-1 -mr-0.5 inline-flex items-center justify-center h-4 w-4 rounded-full bg-orange-200/70 hover:bg-orange-300/80 transition-colors"
            data-testid="dashboard-streak-share-btn"
            aria-label={tx(lang, "Share streak", "Compartir racha", "Compartilhar sequência")}
            title={tx(lang, "Share streak", "Compartir racha", "Compartilhar sequência")}
          >
            <Share2 className="h-2.5 w-2.5" />
          </button>
        )}
      </span>
      <StreakShareCard
        open={!!shareSlug}
        onOpenChange={(v) => { if (!v) setShareSlug(null); }}
        slug={shareSlug}
        userName={userName}
        lang={lang}
      />
    </>
  );
}
